/**
 * Evaluation Page
 * Consolidated evaluation management with tab-based navigation
 * Combines: Eval Scenarios, Eval Metrics, Test Suites
 */

import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { FlaskConical, Ruler, FolderOpen } from 'lucide-react';
import { useI18n } from '@/i18n';
import {
  EvalScenariosPanel,
  EvalMetricsPanel,
  TestSuitesPanel,
  useEvaluationTab,
} from '@/features/evaluation';
import type { EvaluationTab } from '@/features/evaluation';

export function EvaluationPage() {
  const { messages } = useI18n();
  const t = messages.evaluation;
  const { activeTab, setActiveTab } = useEvaluationTab();
  const location = useLocation();
  const navigate = useNavigate();

  // Redirect /evaluation to /evaluation/scenarios
  useEffect(() => {
    if (location.pathname === '/evaluation') {
      navigate('/evaluation/scenarios', { replace: true });
    }
  }, [location.pathname, navigate]);

  return (
    <div className="p-6">
      {/* Page Header - Unified Violet Theme */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t.pageTitle}</h1>
        <p className="text-gray-600 mt-1">{t.pageDescription}</p>
      </div>

      {/* Tabs with Violet/Indigo Theme */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as EvaluationTab)}>
        <TabsList className="mb-6 bg-violet-50 p-1 rounded-lg">
          <TabsTrigger
            value="scenarios"
            className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-violet-700 data-[state=active]:shadow-sm"
          >
            <FlaskConical className="w-4 h-4" />
            {t.tabs.scenarios}
          </TabsTrigger>
          <TabsTrigger
            value="metrics"
            className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-violet-700 data-[state=active]:shadow-sm"
          >
            <Ruler className="w-4 h-4" />
            {t.tabs.metrics}
          </TabsTrigger>
          <TabsTrigger
            value="suites"
            className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-violet-700 data-[state=active]:shadow-sm"
          >
            <FolderOpen className="w-4 h-4" />
            {t.tabs.suites}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="scenarios">
          <EvalScenariosPanel />
        </TabsContent>

        <TabsContent value="metrics">
          <EvalMetricsPanel />
        </TabsContent>

        <TabsContent value="suites">
          <TestSuitesPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}
